import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { devGetMe, ServiceInfo } from '../api/dev';
import '../styles.css';
import Header from '../components/Header';

function decodeJwt(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch { return null; }
}

export default function DevApp() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [services, setServices] = useState<Array<{ id: string; name: string }>>([]);
  const [menuOpen, setMenuOpen] = useState<Record<string, boolean>>({});
  const [svcError, setSvcError] = useState('');
  const envRaw = (import.meta as any)?.env?.VITE_ENV as string | undefined;
  const host = (typeof window !== 'undefined' ? window.location.hostname : '') || '';
  const showDevEnvLabel = !!(
    (envRaw && envRaw.toLowerCase() === 'dev') ||
    host === 'localhost' || host.startsWith('127.') || host.includes('.dev.')
  );
  const base = '/portal';
  const isActive = (p: string) => {
    const full = `${base}${p.startsWith('/') ? p : '/' + p}`;
    return loc.pathname === full || (p === '/health' && (loc.pathname === base || loc.pathname === `${base}/`));
  };
  function signOut() {
    localStorage.removeItem('afw.dev.token');
    window.location.href = '/login';
  }
  const prof = decodeJwt(localStorage.getItem('afw.dev.token') || '');
  if (prof?.mustChange && !loc.pathname.startsWith('/portal/change-password')) {
    navigate('/portal/change-password');
  }
  // Proactively sign out if token expires while user is navigating
  useEffect(() => {
    const check = () => {
      try {
        const t = localStorage.getItem('afw.dev.token') || '';
        if (!t) return;
        const parts = t.split('.');
        if (parts.length < 2) return;
        let p = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        while (p.length % 4 !== 0) p += '=';
        const payload = JSON.parse(atob(p));
        const exp = typeof payload.exp === 'number' ? payload.exp : 0;
        const now = Math.floor(Date.now() / 1000);
        if (exp && exp <= now) {
          try { localStorage.removeItem('afw.dev.token'); } catch {}
          window.location.href = '/login';
        }
      } catch {}
    };
    const id = window.setInterval(check, 30000); // check every 30s
    // Run once on mount
    check();
    return () => { window.clearInterval(id); };
  }, []);
  useEffect(() => {
    (async () => {
      try {
        setSvcError('');
        const me = await devGetMe();
        const list: Array<{ id: string; name: string }> = (me.serviceInfo && me.serviceInfo.length > 0)
          ? me.serviceInfo.map((s: ServiceInfo) => ({ id: s.id, name: s.name }))
          : (me.services || []).map((id: string) => ({ id, name: labelForService(id) }));
        // Always include API Credit Service entry
        if (!list.find(x => x.id === 'api-credit')) list.unshift({ id: 'api-credit', name: labelForService('api-credit') });
        setServices(list);
      } catch (e: any) { setSvcError(e?.message || String(e)); }
    })();
  }, []);

  // Scroll main content to top on route change for better context
  useEffect(() => {
    const scrollTopNow = () => {
      try {
        const el = document.querySelector('.content') as HTMLElement | null;
        if (el) {
          el.scrollTop = 0; // immediate reset for scrollable pane
        }
        // Also reset window/document scroll to cover cases where window scrolls
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
          window.scrollTo(0, 0);
        }
        try { (document.documentElement || ({} as any)).scrollTop = 0; } catch {}
        try { (document.body || ({} as any)).scrollTop = 0; } catch {}
      } catch {}
    };
    // Run immediately and after layout to ensure position is reset
    scrollTopNow();
    requestAnimationFrame(scrollTopNow);
    setTimeout(scrollTopNow, 0);
  }, [loc.pathname]);
  // Auto-collapse other submenus on route change; keep only current service open
  useEffect(() => {
    try {
      const m = /\/services\/([^/]+)/.exec(loc.pathname || '');
      const sid = m ? m[1] : '';
      if (sid === 'lightning-push' || sid === 'forecast-push' || sid === 'history' || sid === 'astronomical' || sid === 'forecast-daily' || sid === 'forecast-hourly') setMenuOpen({ [sid]: true }); else setMenuOpen({});
    } catch {}
  }, [loc.pathname]);
  return (
    <div>
      <Header showPortalLink={false} showLoginLink={false} />
      <div className="layout">
        <aside className="sidebar">
        <div className="brand">
          <img src="/images/logo_small.png" alt="AfricaWeather" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'contain' }} />
          <div>
            <div className="brand-title">Developer Portal</div>
            {showDevEnvLabel && <div className="brand-sub">env: dev</div>}
            {prof?.sub && <div className="muted" style={{ fontSize: 12 }}>Welcome, {prof.sub}</div>}
          </div>
        </div>
        <nav className="menu">
          <div className="menu-section">Monitoring</div>
          <Link className={`menu-item ${isActive('/health') ? 'active' : ''}`} to="health">Health</Link>
          <Link className={`menu-item ${isActive('/usage') ? 'active' : ''}`} to="usage">API Credit Usage</Link>
          {/* Webhook tab removed */}
          <div className="menu-section">Services</div>
          {svcError && <div className="muted" style={{ fontSize: 12 }}>Error: {svcError}</div>}
          {services.length === 0 && !svcError && <div className="muted" style={{ fontSize: 12 }}>No services</div>}
          {services.map(s => {
            const hasSubmenu = (s.id === 'lightning-push' || s.id === 'forecast-push' || s.id === 'history' || s.id === 'astronomical' || s.id === 'forecast-daily' || s.id === 'forecast-hourly');
            const isUnder = loc.pathname.includes(`/services/${s.id}`);
            const isOpen = (menuOpen[s.id] !== undefined) ? !!menuOpen[s.id] : isUnder;
            const toggle = () => setMenuOpen(() => ({ [s.id]: !isOpen }));
            return (
              <div key={s.id} className="menu-group">
                {hasSubmenu ? (
                  <div
                    className={`menu-item toggleable ${isUnder ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                  >
                    <span>{s.name}</span>
                    <span className={`submenu-toggle ${isOpen ? 'expanded' : ''}`} aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="8 4 16 12 8 20"></polyline>
                      </svg>
                    </span>
                  </div>
                ) : (
                  <Link className={`menu-item ${isActive(`/services/${s.id}`) ? 'active' : ''}`} to={`services/${s.id}`}>{s.name}</Link>
                )}
                {hasSubmenu && isOpen && (
                  <>
                    {s.id === 'lightning-push' ? (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/lightning-push') ? 'active' : ''}`} to={`services/lightning-push`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/lightning-push/access') ? 'active' : ''}`} to={`services/lightning-push/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/lightning-push/webhook') ? 'active' : ''}`} to={`services/lightning-push/webhook`}>Webhook</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/lightning-push/regions') ? 'active' : ''}`} to={`services/lightning-push/regions`}>Regions</Link>
                      </>
                    ) : s.id === 'forecast-push' ? (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push') ? 'active' : ''}`} to={`services/forecast-push`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push/access') ? 'active' : ''}`} to={`services/forecast-push/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push/locations') ? 'active' : ''}`} to={`services/forecast-push/locations`}>Locations</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push/rules') ? 'active' : ''}`} to={`services/forecast-push/rules`}>Rules</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push/webhook') ? 'active' : ''}`} to={`services/forecast-push/webhook`}>Webhook</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-push/webhook-test') ? 'active' : ''}`} to={`services/forecast-push/webhook-test`}>Webhook Test</Link>
                      </>
                    ) : s.id === 'history' ? (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/history') ? 'active' : ''}`} to={`services/history`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/history/access') ? 'active' : ''}`} to={`services/history/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/history/api') ? 'active' : ''}`} to={`services/history/api`}>API</Link>
                      </>
                    ) : s.id === 'forecast-daily' ? (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-daily') ? 'active' : ''}`} to={`services/forecast-daily`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-daily/access') ? 'active' : ''}`} to={`services/forecast-daily/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-daily/api') ? 'active' : ''}`} to={`services/forecast-daily/api`}>API</Link>
                      </>
                    ) : s.id === 'forecast-hourly' ? (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-hourly') ? 'active' : ''}`} to={`services/forecast-hourly`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-hourly/access') ? 'active' : ''}`} to={`services/forecast-hourly/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/forecast-hourly/api') ? 'active' : ''}`} to={`services/forecast-hourly/api`}>API</Link>
                      </>
                    ) : (
                      <>
                        <Link className={`menu-item menu-subitem ${isActive('/services/astronomical') ? 'active' : ''}`} to={`services/astronomical`}>About</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/astronomical/access') ? 'active' : ''}`} to={`services/astronomical/access`}>Access</Link>
                        <Link className={`menu-item menu-subitem ${isActive('/services/astronomical/api') ? 'active' : ''}`} to={`services/astronomical/api`}>API</Link>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
          <div className="menu-section">Account</div>
          <Link className={`menu-item ${isActive('/keys') ? 'active' : ''}`} to="keys">API Keys</Link>
          <Link className={`menu-item ${isActive('/credit') ? 'active' : ''}`} to="credit">API Credit</Link>
          <Link className={`menu-item ${isActive('/change-password') ? 'active' : ''}`} to="change-password">Security</Link>
          <div style={{ height: 12 }} />
          <button className="btn danger" onClick={signOut}>Sign out</button>
        </nav>
      </aside>
      <main className="content">
        <Outlet />
      </main>
      </div>
    </div>
  );
}

  function labelForService(id: string): string {
    switch (id) {
    case 'api-credit': return 'API Credit Service';
      case 'current': return 'Current API';
      case 'forecast-hourly': return 'Forecast Hourly API';
      case 'forecast-daily': return 'Forecast Daily';
      case 'history': return 'History API';
      case 'astronomical': return 'Astronomical API';
      case 'bulk-current': return 'Bulk Current';
      case 'bulk-forecast-hourly': return 'Bulk Forecast Hourly';
      default: return id;
    }
  }
